import { applyInvoiceRoundOff, roundMoney } from './amountUtils';

const STATE_NAME_TO_CODE = {
  'jammu and kashmir': '01',
  'jammu & kashmir': '01',
  'himachal pradesh': '02',
  punjab: '03',
  chandigarh: '04',
  uttarakhand: '05',
  uttaranchal: '05',
  haryana: '06',
  delhi: '07',
  rajasthan: '08',
  'uttar pradesh': '09',
  bihar: '10',
  sikkim: '11',
  'arunachal pradesh': '12',
  nagaland: '13',
  manipur: '14',
  mizoram: '15',
  tripura: '16',
  meghalaya: '17',
  assam: '18',
  'west bengal': '19',
  jharkhand: '20',
  odisha: '21',
  orissa: '21',
  chhattisgarh: '22',
  'madhya pradesh': '23',
  gujarat: '24',
  'dadra and nagar haveli and daman and diu': '26',
  maharashtra: '27',
  maharastra: '27',
  'andhra pradesh': '28',
  karnataka: '29',
  goa: '30',
  lakshadweep: '31',
  kerala: '32',
  'tamil nadu': '33',
  puducherry: '34',
  pondicherry: '34',
  'andaman and nicobar islands': '35',
  telangana: '36',
  ladakh: '38',
};

// Union territories without a legislature use UTGST. Delhi, Puducherry and Jammu & Kashmir use SGST.
const UNION_TERRITORIES = ['04', '26', '31', '35', '38'];

export function extractStateCode(gstin) {
  if (!gstin || typeof gstin !== 'string') return null;
  const clean = gstin.trim().toUpperCase();
  if (clean.length !== 15) return null;
  const code = clean.substring(0, 2);
  return /^\d{2}$/.test(code) ? code : null;
}

export function resolveStateCode(value) {
  if (value === null || value === undefined) return null;
  const input = String(value).trim();
  if (!input) return null;
  if (/^\d{2}$/.test(input)) return input;

  const bracketMatch = input.match(/\[(\d{2})\]/);
  if (bracketMatch) return bracketMatch[1];

  const normalizedName = input
    .toLowerCase()
    .replace(/\[\d{2}\]/g, '')
    .replace(/[^a-z\s&()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (STATE_NAME_TO_CODE[normalizedName]) {
    return STATE_NAME_TO_CODE[normalizedName];
  }

  for (const [name, code] of Object.entries(STATE_NAME_TO_CODE)) {
    if (normalizedName.includes(name) || name.includes(normalizedName)) {
      return code;
    }
  }

  return null;
}

export function determineGSTType({
  supplierGSTIN,
  customerGSTIN,
  customerCategory = 'Domestic',
  placeOfSupply = null,
  supplierState = null,
  isLUTBond = false,
}) {
  if (customerCategory === 'Export' || customerCategory === 'SEZ') {
    if (isLUTBond) {
      return { type: 'ZERO_RATED', applyIGST: false, applyCGST: false, applySGST: false, applyUTGST: false };
    }
    return { type: 'IGST', applyIGST: true, applyCGST: false, applySGST: false, applyUTGST: false };
  }

  const supplierStateCode = extractStateCode(supplierGSTIN) || resolveStateCode(supplierState);
  let customerStateCode = extractStateCode(customerGSTIN);
  if (!customerStateCode && placeOfSupply) {
    customerStateCode = resolveStateCode(placeOfSupply);
  }

  if (!supplierStateCode || !customerStateCode) {
    return { type: 'IGST', applyIGST: true, applyCGST: false, applySGST: false, applyUTGST: false };
  }

  if (supplierStateCode === customerStateCode) {
    if (UNION_TERRITORIES.includes(customerStateCode)) {
      return { type: 'CGST_UTGST', applyIGST: false, applyCGST: true, applySGST: false, applyUTGST: true };
    }
    return { type: 'CGST_SGST', applyIGST: false, applyCGST: true, applySGST: true, applyUTGST: false };
  }

  return { type: 'IGST', applyIGST: true, applyCGST: false, applySGST: false, applyUTGST: false };
}

export function calculateGSTAmounts(taxableValue, gstRate, gstConfig, rates = {}) {
  const cgstRate = rates.cgstRate ?? gstRate / 2;
  const sgstRate = rates.sgstRate ?? gstRate / 2;
  const igstRate = rates.igstRate ?? gstRate;

  if (gstConfig.type === 'ZERO_RATED') {
    return {
      cgstRate: 0, cgstAmount: 0,
      sgstRate: 0, sgstAmount: 0,
      utgstRate: 0, utgstAmount: 0,
      igstRate: 0, igstAmount: 0,
      totalTax: 0,
      gstType: gstConfig.type,
    };
  }

  const totalTaxAmount = (taxableValue * gstRate) / 100;

  if (gstConfig.type === 'IGST') {
    const igstAmount = roundMoney((taxableValue * igstRate) / 100);
    return {
      cgstRate: 0, cgstAmount: 0,
      sgstRate: 0, sgstAmount: 0,
      utgstRate: 0, utgstAmount: 0,
      igstRate, igstAmount,
      totalTax: igstAmount,
      gstType: gstConfig.type,
    };
  }

  if (gstConfig.type === 'CGST_UTGST') {
    const cgstAmount = roundMoney((taxableValue * cgstRate) / 100);
    const utgstAmount = roundMoney((taxableValue * cgstRate) / 100);
    return {
      cgstRate, cgstAmount,
      sgstRate: 0, sgstAmount: 0,
      utgstRate: cgstRate, utgstAmount,
      igstRate: 0, igstAmount: 0,
      totalTax: roundMoney(cgstAmount + utgstAmount),
      gstType: gstConfig.type,
    };
  }

  // CGST_SGST (intra-state)
  const cgstAmount = roundMoney((taxableValue * cgstRate) / 100);
  const sgstAmount = roundMoney((taxableValue * sgstRate) / 100);
  return {
    cgstRate, cgstAmount,
    sgstRate, sgstAmount,
    utgstRate: 0, utgstAmount: 0,
    igstRate: 0, igstAmount: 0,
    totalTax: roundMoney(cgstAmount + sgstAmount),
    gstType: gstConfig.type,
  };
}

export function calculateInvoiceGST({
  subtotal,
  supplierGSTIN,
  customerGSTIN,
  customerCategory = 'Domestic',
  placeOfSupply = null,
  supplierState = null,
  isLUTBond = false,
  gstRate = 18,
  cgstRate = 9,
  sgstRate = 9,
  igstRate = 18,
}) {
  if (!supplierGSTIN || subtotal <= 0) {
    return {
      subtotal,
      cgstAmount: 0,
      sgstAmount: 0,
      utgstAmount: 0,
      igstAmount: 0,
      totalTaxAmount: 0,
      totalAmountBeforeRoundOff: subtotal,
      roundOffAmount: 0,
      totalAmount: subtotal,
      gstType: 'IGST',
    };
  }

  const gstConfig = determineGSTType({
    supplierGSTIN,
    customerGSTIN,
    customerCategory,
    placeOfSupply,
    supplierState,
    isLUTBond,
  });

  const amounts = calculateGSTAmounts(subtotal, gstRate, gstConfig, { cgstRate, sgstRate, igstRate });
  const finalized = applyInvoiceRoundOff(subtotal, amounts.totalTax);

  return {
    subtotal: finalized.totalValue,
    cgstAmount: amounts.cgstAmount,
    sgstAmount: amounts.sgstAmount,
    utgstAmount: amounts.utgstAmount,
    igstAmount: amounts.igstAmount,
    totalTaxAmount: finalized.totalTaxAmount,
    totalAmountBeforeRoundOff: finalized.totalAmountBeforeRoundOff,
    roundOffAmount: finalized.roundOffAmount,
    totalAmount: finalized.totalAmountWithTax,
    gstType: amounts.gstType,
  };
}
